import { EnhancedDriverData, FormulaMonthlyBudget } from '../../driverData/types/driverData'
import { AllocationBudgetRow, MonthlyBudget } from '../types'
import { DEFAULT_ALLOCATION_METHOD } from '../constants'
import { StatementColumn, StatementDataType } from '../../financialStatements/types'
import { applyAllocation, convertMonthlyBudgets } from './conversionUtils'

/**
 * Converts EnhancedDriverData[] to AllocationBudgetRow[]
 *
 * Flattens newBudgets.groups and newActuals.groups entries - each group entry becomes a separate row.
 * This allows user to configure allocation method for each aggregated cell individually.
 *
 * @param rowDatas - Grid rows with newBudgets/newActuals.groups changes
 * @param columns - Grid columns (needed to map dataKey → periodGroup)
 * @returns Array of allocation rows for modal
 */
export const convertDriverDataToAllocationRows = (
  rowDatas: EnhancedDriverData[],
  columns: StatementColumn[]
): AllocationBudgetRow[] => {
  const allocationRows: AllocationBudgetRow[] = []

  // Build dataKey → column lookup for groups columns only
  const groupColumnsMap = new Map<number, StatementColumn>()
  columns
    .filter(col => col.dataSource === 'groups' && col.dataKey != null)
    .forEach(col => {
      groupColumnsMap.set(col.dataKey!, col)
    })

  // Process each row
  rowDatas.forEach(rowData => {
    const { formulaId, driverName, dimensionId, dimensionName, newBudgets, newActuals, actuals, variableType, style } =
      rowData

    // Process newBudgets.groups (budget data type)
    if (newBudgets?.groups) {
      Object.entries(newBudgets.groups).forEach(([dataKeyStr, value]) => {
        const dataKey = parseInt(dataKeyStr, 10)
        const column = groupColumnsMap.get(dataKey)

        if (!column || !column.periodGroup) {
          console.warn(`No column found for dataKey ${dataKey}`)
          return
        }

        const { periodGroup } = column
        const targetPeriods = periodGroup.periods || []

        // Create unique ID: formulaId_dimensionId_dataKey_budget
        const id = `${formulaId}_${dimensionId}_${dataKey}_budget`

        // If value is null, mark as deletion (remove all budgets for this period)
        const isDeletion = value == null

        const row: AllocationBudgetRow = {
          id,
          entityType: 'formula',
          accountCode: formulaId, // Use formulaId as accountCode equivalent
          accountName: driverName,
          dimensionId,
          dimensionName,
          actuals,
          dataType: StatementDataType.budget,
          variableType, // Use formula's VariableType directly
          style: {
            decimals: style?.decimals ?? (variableType === 'percentage' ? 1 : 0) // Type-based defaults: percentage=1, absolute=0
          },
          dataKey,
          periodGroup,
          targetPeriods,
          periodLabel: column.label,
          periodLength: targetPeriods.length,
          value: isDeletion ? 0 : value,
          isDeletion,
          allocationMethod: DEFAULT_ALLOCATION_METHOD,
          allocationOptions: undefined
        }

        allocationRows.push(row)
      })
    }

    // Process newActuals.groups (actuals data type)
    if (newActuals?.groups) {
      Object.entries(newActuals.groups).forEach(([dataKeyStr, value]) => {
        const dataKey = parseInt(dataKeyStr, 10)
        const column = groupColumnsMap.get(dataKey)

        if (!column || !column.periodGroup) {
          console.warn(`No column found for dataKey ${dataKey}`)
          return
        }

        const { periodGroup } = column
        const targetPeriods = periodGroup.periods || []

        // Create unique ID: formulaId_dimensionId_dataKey_actuals
        const id = `${formulaId}_${dimensionId}_${dataKey}_actuals`

        // If value is null, mark as deletion
        const isDeletion = value == null

        const row: AllocationBudgetRow = {
          id,
          entityType: 'formula',
          accountCode: formulaId, // Use formulaId as accountCode equivalent
          accountName: driverName,
          dimensionId,
          dimensionName,
          actuals,
          dataType: StatementDataType.actuals,
          variableType, // Use formula's VariableType directly
          style: {
            decimals: style?.decimals ?? (variableType === 'percentage' ? 1 : 0) // Type-based defaults: percentage=1, absolute=0
          },
          dataKey,
          periodGroup,
          targetPeriods,
          periodLabel: column.label,
          periodLength: targetPeriods.length,
          value: isDeletion ? 0 : value,
          isDeletion,
          allocationMethod: DEFAULT_ALLOCATION_METHOD,
          allocationOptions: undefined
        }

        allocationRows.push(row)
      })
    }
  })

  return allocationRows
}

/**
 * Converts MonthlyBudget array to FormulaMonthlyBudget array by adding formula context.
 * This is the per-row converter used by applyAllocation.
 *
 * @param monthlyBudgets - Array of monthly budget allocations (output from allocation functions)
 * @param row - Allocation row containing formulaId (in accountCode), dimensionId, and dataType
 * @returns Array of FormulaMonthlyBudget ready for backend API
 */
export const convertMonthlyBudgetsToFormulaData = (
  monthlyBudgets: MonthlyBudget[],
  row: AllocationBudgetRow
): FormulaMonthlyBudget[] => {
  // Normalize dimensionId: convert "null" string to null (enterprise level)
  const normalizedDimensionId = row.dimensionId === 'null' ? null : row.dimensionId!

  return convertMonthlyBudgets<FormulaMonthlyBudget>(
    monthlyBudgets,
    row.accountCode, // accountCode contains formulaId
    normalizedDimensionId,
    row.dataType as StatementDataType,
    'formulaId'
  )
}

/**
 * Converts AllocationBudgetRow[] to FormulaMonthlyBudget[]
 *
 * Uses applyAllocation with convertMonthlyBudgetsToFormulaData converter.
 *
 * @param allocationRows - Rows with configured allocation methods
 * @returns Array of monthly budgets ready for backend API
 */
export const convertAllocationToFormulaData = (allocationRows: AllocationBudgetRow[]): FormulaMonthlyBudget[] => {
  return applyAllocation<FormulaMonthlyBudget>(allocationRows, convertMonthlyBudgetsToFormulaData)
}
